import { Editor, Range } from '@tiptap/core';
import { EditorState, Plugin, PluginKey, PluginView } from '@tiptap/pm/state';
import { EditorView } from '@tiptap/pm/view';
import { Slice } from 'prosemirror-model';

type PluginState<Data> = {
  open: boolean;
  data?: Data;
  range?: Range;
};

export type RendererHookProps<Data> = {
  editor: Editor;
  data: Data;
  range: Range;
  command: () => void;
  onClose: () => void;
  clientRect: () => DOMRect;
};

export type FindFn<Data> = (
  text: string,
  slice: Slice,
  event: ClipboardEvent,
) => Data | null;

export type Renderer<Data> = {
  onStart?(props: RendererHookProps<Data>): void;
  onExit?(): void;
  onBeforeStart?(props: RendererHookProps<Data>): void;
  onBeforeUpdate?(props: RendererHookProps<Data>): void;
  onUpdate?(props: RendererHookProps<Data>): void;
};

class AfterPastePluginView<Data> implements PluginView {
  constructor(
    private view: EditorView,
    private key: PluginKey,
    private editor: Editor,
    private renderHandle: Renderer<Data>,
  ) {
    this.update(view, {} as any);
  }

  update(view: EditorView, prevState: EditorState) {
    const { state } = view;
    const prev = this.key?.getState(prevState);
    const next = this.key?.getState(view.state);

    // See how the state changed
    const moved =
      prev?.open && next?.open && prev?.range?.from !== next?.range?.from;
    const started = !prev?.open && next?.open;
    const stopped = prev?.open && !next?.open;
    // const changed = !started && !stopped && prev.query !== next.query;
    const handleStart = started || moved;
    // const handleChange = changed && !moved;
    const handleExit = stopped || moved;

    const { to } = state.selection;

    const props: RendererHookProps<Data> = {
      editor: this.editor,
      data: next?.data,
      range: next?.range,
      command: () => {},
      onClose: () => {
        view.dispatch(view.state.tr.setMeta(this.key, { open: false }));
      },
      clientRect: () => {
        const cors = view.coordsAtPos(to);
        const rects: DOMRect = {
          bottom: cors.bottom,
          left: cors.left,
          right: cors.right,
          top: cors.top,
          width: cors.right - cors.left,
          height: cors.bottom - cors.top,
          x: cors.left,
          y: cors.top,
          toJSON: () => ({}),
        };
        return rects;
      },
    };

    if (handleStart) {
      this.renderHandle?.onBeforeStart?.(props);
    }

    // if (handleChange) {
    //   this.renderHandle?.onBeforeUpdate?.(props);
    // }

    // if (handleChange || handleStart) {
    //   props.items = await items({
    //     editor,
    //     query: state.query,
    //   })
    // }

    if (handleExit) {
      this.renderHandle?.onExit?.();
    }

    // if (handleChange) {
    //   this.renderHandle?.onUpdate?.(props);
    // }

    if (handleStart) {
      this.renderHandle?.onStart?.(props);
    }

    // // Otherwise, reposition it and update its content
    // this.tooltip.style.display = '';
    // const { from, to } = state.selection;

    // // These are in screen coordinates
    // const start = view.coordsAtPos(from);
    // const end = view.coordsAtPos(to);

    // // The box in which the tooltip is positioned, to use as base
    // const box = this.tooltip.offsetParent?.getBoundingClientRect();
    // if (!box) return;

    // // Find a center-ish x position from the selection endpoints (when
    // // crossing lines, end may be more to the left)
    // const left = Math.max((start.left + end.left) / 2, start.left + 3);
    // this.tooltip.style.left = `${left - box.left}px`;
    // this.tooltip.style.bottom = `${box.bottom - start.top}px`;
    // this.tooltip.textContent = (to - from).toString();
  }

  destroy() {
    this.renderHandle?.onExit?.();
  }
}

export const createAfterPastePlugin = <Data>({
  pluginKey,
  editor,
  render,
  find,
}: {
  pluginKey: PluginKey;
  editor: Editor;
  render: () => Renderer<Data>;
  find: FindFn<Data>;
}) => {
  const renderHandle = render();

  return new Plugin<PluginState<Data>>({
    key: pluginKey,
    view: (view) =>
      new AfterPastePluginView(view, pluginKey, editor, renderHandle),
    state: {
      init() {
        return { open: false };
      },
      apply(tr, value) {
        const newPluginState = tr.getMeta(pluginKey);
        if (newPluginState) {
          return newPluginState;
        }
        return value;
      },
    },
    props: {
      // Call the keydown hook if suggestion is active.
      handleKeyDown(view, event) {
        const { open } = pluginKey.getState(view.state);

        if (!open) {
          return false;
        }

        view.dispatch(view.state.tr.setMeta(pluginKey, { open: false }));

        // return renderHandle?.onKeyDown?.({ view, event, range }) || false;
        return false;
      },
      handlePaste: (view, event, slice) => {
        const { state } = view;
        const { selection } = state;

        let textContent = '';

        slice.content.forEach((node) => {
          textContent += node.textContent;
        });

        const data = find(textContent, slice, event);

        if (data) {
          const nextState: PluginState<Data> = {
            open: true,
            data,
            range: {
              from: selection.from,
              to: selection.to + textContent.length,
            },
          };

          view.dispatch(view.state.tr.setMeta(pluginKey, nextState));
        }

        return false;
      },
    },
  });
};
